import { readMeteringDenial } from '@/lib/credits/read-denial';

/**
 * Thrown when the API returns a usage limit error (e.g. message or token limit reached).
 * Used to display a user-friendly message for freemium account users.
 */
export class UsageLimitError extends Error {
    currentCount?: number;
    limit?: number;
    limitType?: string;
    daysUntilReset?: number | null;
    limitMessage?: string | null;

    constructor(
        message: string,
        details: {
            currentCount?: number;
            limit?: number;
            limitType?: string;
            daysUntilReset?: number | null;
            limitMessage?: string | null;
        } = {},
    ) {
        super(message);
        this.name = 'UsageLimitError';
        this.currentCount = details.currentCount;
        this.limit = details.limit;
        this.limitType = details.limitType;
        this.daysUntilReset = details.daysUntilReset;
        this.limitMessage = details.limitMessage;
    }
}

export class CreditLimitError extends Error {
    reason:
        | 'monthly_cap_exceeded'
        | 'annual_pool_exceeded'
        | 'license_expired'
        | 'no_credits'
        | 'user_spend_limit_exceeded';
    resetDate?: string;
    licenseEndDate?: string | null;
    monthlyRemaining?: number;
    annualRemaining?: number;
    annualConsumed?: number;
    annualPool?: number;
    userSpendLimit?: number;
    userMonthToDate?: number;

    constructor(
        message: string,
        details: {
            reason:
                | 'monthly_cap_exceeded'
                | 'annual_pool_exceeded'
                | 'license_expired'
                | 'no_credits'
                | 'user_spend_limit_exceeded';
            resetDate?: string;
            licenseEndDate?: string | null;
            monthlyRemaining?: number;
            annualRemaining?: number;
            annualConsumed?: number;
            annualPool?: number;
            userSpendLimit?: number;
            userMonthToDate?: number;
        },
    ) {
        super(message);
        this.name = 'CreditLimitError';
        this.reason = details.reason;
        this.resetDate = details.resetDate;
        this.licenseEndDate = details.licenseEndDate;
        this.monthlyRemaining = details.monthlyRemaining;
        this.annualRemaining = details.annualRemaining;
        this.annualConsumed = details.annualConsumed;
        this.annualPool = details.annualPool;
        this.userSpendLimit = details.userSpendLimit;
        this.userMonthToDate = details.userMonthToDate;
    }
}

type ChatCreditReason = CreditLimitError['reason'];

/**
 * Turn a chat error body into the error DANI already catches.
 * The reader decides it is a metering denial. The thrown classes stay
 * CreditLimitError and UsageLimitError so the existing instanceof checks
 * do not change. A reason the reader does not hand to CreditLimitError
 * still throws that error, which is what the chat string did before.
 */
export function errorFromChatDenialBody(
    errorData: unknown,
): CreditLimitError | UsageLimitError | null {
    const denial = readMeteringDenial(errorData);
    if (!errorData || typeof errorData !== 'object') return null;
    const body = errorData as Record<string, unknown>;

    if (body.error === 'Credit limit reached') {
        const reason =
            denial?.kind === 'credit_limit' && denial.reason !== 'insufficient_for_action'
                ? denial.reason
                : (body.reason as ChatCreditReason);
        const message = typeof body.message === 'string' ? body.message : '';
        return new CreditLimitError(message || 'Credit limit reached', {
            reason,
            resetDate: body.resetDate as string | undefined,
            licenseEndDate: body.licenseEndDate as string | null | undefined,
            monthlyRemaining: body.monthlyRemaining as number | undefined,
            annualRemaining: body.annualRemaining as number | undefined,
            annualConsumed: body.annualConsumed as number | undefined,
            annualPool: body.annualPool as number | undefined,
            userSpendLimit: body.userSpendLimit as number | undefined,
            userMonthToDate: body.userMonthToDate as number | undefined,
        });
    }

    if (body.error === 'Usage limit reached' && denial?.kind === 'freemium_limit') {
        const message = typeof body.message === 'string' ? body.message : '';
        return new UsageLimitError(message || 'Usage limit reached', {
            currentCount: body.currentCount as number | undefined,
            limit: body.limit as number | undefined,
            limitType: body.limitType as string | undefined,
            daysUntilReset: body.daysUntilReset as number | null | undefined,
            limitMessage: body.limitMessage as string | null | undefined,
        });
    }

    return null;
}
